"use client";
import { useEffect, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import {
  Box,
  CardActionArea,
  Container,
  Divider,
  Stack,
  TextField,
  Typography,
  InputAdornment
} from "@mui/material";
import {
  EmailRounded as EmailIcon,
  GitHub as GitHubIcon,
  PasswordRounded as PasswordIcon
} from "@mui/icons-material";
import {
  getAuth,
  signInWithEmailAndPassword,
  EmailAuthProvider,
  linkWithCredential
} from "firebase/auth";

import { observer, useStore } from "../../service/mobx";
import { DiscordIcon } from "../../component/Icon/Discord";
import ButtonFilled from "../../component/Button/Filled";

function Plan() {
  const { user } = useStore();
  const router = useRouter();
  const returnTo = useSearchParams().get("returnTo") ?? "/";
  const signedIn = user.loaded && user.isAnonymous === false;

  useEffect(() => {
    if (signedIn) {
      router.replace(returnTo);
    }
  }, [router, returnTo, signedIn]);

  return user.loaded === false || signedIn ? null : <LogIn />;
}

export default observer(Plan);

const LogIn = observer(function LogIn() {
  const [mode, setMode] = useState("sign-up");
  const [form, setForm] = useState({ email: "", password: "", error: {} });
  const { analytics, user } = useStore();
  const [options] = useState([
    {
      img: "https://raw.githubusercontent.com/firebase/firebaseui-web/master/image/google.svg",
      label: "Google",
      color: "var(--tertiary-color)",
      onClick: user.sign.in.google
    },
    {
      Icon: DiscordIcon,
      label: "Discord",
      color: "var(--colors-info-60)",
      onClick: user.sign.in.discord
    },
    {
      Icon: GitHubIcon,
      label: "Github",
      color: "var(--surface-on-variant)",
      onClick: user.sign.in.github
    }
  ]);
  const submit = async () => {
    try {
      // remove any  errors
      setForm(form => ({ error: {}, ...form }));

      if (mode === "sign-up") {
        // create account
        await linkWithCredential(
          getAuth().currentUser,
          EmailAuthProvider.credential(form.email, form.password)
        );
      }

      // Regular sign-in
      const session = await signInWithEmailAndPassword(
        getAuth(),
        form.email,
        form.password
      );

      user.set.session(session.user);

      analytics.signIn("email");
    } catch ({ code }) {
      const [, error] = code.split("/");
      const nameSpace = error.includes("email") ? "email" : "password";

      setForm(form => ({
        ...form,
        error: { [nameSpace]: error.replaceAll("-", " ") }
      }));
    }
  };
  const dev = process.env.NODE_ENV === "development";

  return (
    <Container
      maxWidth="medium"
      sx={{
        height: "100dvh",
        display: "flex",
        alignItems: "center",
        flexDirection: "column",
        justifyContent: "center"
      }}
    >
      <Typography
        typography="brand"
        translate="no"
        fontSize={56}
        align="center"
        mt={7}
        mb={4}
        color="var(--secondary-color)"
        fontWeight={700}
      >
        bytez
      </Typography>
      <Box
        p={2}
        maxWidth={420}
        width="100%"
        mb={4}
        borderRadius="var(--shape-sm)"
        bgcolor="var(--surface-container-high)"
        sx={{
          transition: "all 500ms var(--motion-easing-emphasized)",
          "&:hover": {
            bgcolor: "var(--surface-container-color)"
          }
        }}
      >
        <Typography variant="headlineMd" fontWeight={500}>
          {/* {mode === "sign-in" ? "Log In" : "Sign up"} */}
          Sign in
        </Typography>
        {dev ? (
          <Typography component="p" color="var(--secondary-color)">
            {`${
              mode === "sign-in"
                ? "Don't have an account?"
                : "Already have an account?"
            } `}
            <Box
              component="span"
              onClick={() =>
                setMode(mode === "sign-in" ? "sign-up" : "sign-in")
              }
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              {`Sign ${mode === "sign-in" ? "up" : "in"}`}
            </Box>
          </Typography>
        ) : null}
        <Stack spacing={1} mt={4}>
          {options.map(({ Icon, img, label, color, onClick }) => (
            <CardActionArea key={label}>
              <Stack
                key={label}
                direction="row"
                alignItems="center"
                onClick={onClick}
                border={1}
                px={1}
                py={1}
                bgcolor="var(--surface-container-low)"
                borderRadius="var(--shape-xs-round)"
                borderColor="var(--outline-variant)"
              >
                {img ? (
                  <img
                    width={24}
                    height={24}
                    alt="Sign in with Google"
                    src={img}
                  />
                ) : (
                  <Icon sx={{ width: 24, height: 24, color }} />
                )}

                <Typography
                  width="100%"
                  align="center"
                  typography="titleMd"
                  fontWeight={600}
                >
                  {`Sign in with ${label}`}
                </Typography>
              </Stack>
            </CardActionArea>
          ))}
        </Stack>
        {dev ? (
          <>
            <Divider sx={{ my: 4 }}>
              <Typography
                typography="titleSm"
                color="var(--secondary-color)"
                sx={{ opacity: 0.7 }}
              >
                or
              </Typography>
            </Divider>
            <Stack spacing={2}>
              <Input
                label="Email address"
                type="email"
                Icon={EmailIcon}
                placeholder="Email"
                setForm={setForm}
                helperText={form.error.email}
                submit={submit}
              />
              <Input
                label="Password"
                type="password"
                Icon={PasswordIcon}
                setForm={setForm}
                placeholder="Password"
                helperText={form.error.password}
                submit={submit}
              />
              <ButtonFilled
                fullWidth
                label={`Sign ${mode === "sign-in" ? "In" : "Up"}`}
                sx={{
                  height: 48,
                  fontWeight: 700,
                  typography: "titleMd",
                  borderRadius: "var(--shape-sm)",
                  bgcolor: "var(--primary-container)",
                  color: "var(--primary-on-container)",
                  "&:hover": {
                    bgcolor: "var(--primary-color)",
                    color: "var(--primary-on-color)"
                  }
                }}
                onClick={submit}
              />
            </Stack>
          </>
        ) : null}
        <Typography
          mt={2}
          component="p"
          variant="bodySm"
          sx={{ opacity: 0.7 }}
          color="var(--secondary-color)"
        >
          By continuing, you agree to{" "}
          <Box
            component="a"
            target="_blank"
            color="var(--secondary-color)"
            href="https://cdn.bytez.com/tos.html"
          >
            terms
          </Box>{" "}
          and{" "}
          <Box
            component="a"
            target="_blank"
            color="var(--secondary-color)"
            href="https://cdn.bytez.com/privacy.html"
          >
            policies
          </Box>
        </Typography>
      </Box>
    </Container>
  );
});

const Input = ({
  Icon,
  label,
  type,
  placeholder,
  setForm,
  helperText,
  submit
}) => (
  <TextField
    fullWidth
    size="small"
    variant="filled"
    label={label}
    type={type}
    helperText={helperText}
    error={!!helperText}
    onChange={event =>
      setForm(form => ({ ...form, [type]: event.target.value }))
    }
    placeholder={placeholder}
    onKeyUp={event => {
      if (event.key === "Enter") {
        submit();
      }
    }}
    slotProps={{
      input: {
        disableUnderline: true,
        sx: {
          borderRadius: "var(--shape-md-round)",
          bgcolor: "var(--surface-container-lowest)"
        },
        startAdornment: (
          <InputAdornment position="start">
            <Icon
              sx={{
                width: 24,
                height: 24,
                color: "var(--surface-on-variant)"
              }}
            />
          </InputAdornment>
        )
      }
    }}
  />
);
